// -*- coding: utf-8 -*-
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { apiUrl, fetchJson } from '../../apiBase';

const DEFAULT_TIME = '17:00';
const WEEKLY_LOCKED_DAYS = ['mon'];

const DAY_OPTIONS = [
  { key: 'mon', label: 'Pzt' },
  { key: 'tue', label: 'Sal' },
  { key: 'wed', label: 'Çar' },
  { key: 'thu', label: 'Per' },
  { key: 'fri', label: 'Cum' },
  { key: 'sat', label: 'Cmt' },
  { key: 'sun', label: 'Paz' },
];

const REMINDER_TYPES = [
  { key: 'reading_speed', label: 'Okuma Hızı Testi – Haftalık' },
  { key: 'study_log', label: 'Günlük Çalışma' },
  { key: 'finished_books', label: 'Tamamlanan Kitap (son 5 gün kaydı)' },
  { key: 'mini_task', label: 'Mini Görev (son 18 saat tamamlanan görev kontrolü)' },
  { key: 'weekly_engagement', label: 'Haftalık Veli Özeti (son 7 gün karşılaştırma)' },
];

const REMINDER_TYPE_HELP = {
  reading_speed:
    'Kontrol: Bu hafta veli tarafından okuma hızı sonucu girilmiş mi? Yoksa hatırlatma gider. Örnek veli mesajı: "Okuma hızı testi: 85 kelime"',
  study_log:
    'Yalnızca 4–8. sınıflar için: Bugün soru, okunan sayfa veya tamamlanan kitap kaydı yoksa hatırlatma gider. Veli açılan formdan çalışma ve okuma bilgilerini ekler.',
  finished_books:
    'Kontrol: Son 5 günde tamamlanan kitap kaydı var mı? Yoksa hatırlatma gider. Örnek veli mesajı: "Şeker Portakalı kitabını bitirdi"',
  mini_task:
    'Kontrol: Son 18 saatte tamamlanan mini görev roundu var mı? Yoksa hatırlatma gider. 24 saat içindeyse butonlu mesaj, değilse şablon butonu gider; veli butona basınca taze mini görev linki üretilir.',
  weekly_engagement:
    'Kontrol: Haftalık özet haftalık plana göre gönderilir. 24 saat içindeyse detaylı metin, değilse şablon mesaj kullanılır.',
};

const TARGET_SCOPES = [
  { key: 'classes', label: 'Şubelere göre' },
  { key: 'grade_levels', label: 'Sınıf seviyesine göre' },
];

const HOURLY_TIME_OPTIONS = Array.from({ length: 24 }, (_, hour) => {
  const value = `${String(hour).padStart(2, '0')}:00`;
  return { value, label: value };
});

function normalizeTimeString(value, fallback = '00:00') {
  const raw = String(value || '').trim();
  if (!raw) return fallback;

  const match = raw.match(/^(\d{1,2})(?::(\d{1,2}))?(?::\d{1,2})?$/);
  if (!match) return fallback;

  const hour = Math.min(23, Math.max(0, parseInt(match[1] || '0', 10)));
  const minute = Math.min(59, Math.max(0, parseInt(match[2] || '0', 10)));
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function normalizeHourlyTimeString(value, fallback = '00:00') {
  const normalized = normalizeTimeString(value, fallback);
  const hour = String(normalized || fallback).split(':')[0] || '00';
  return `${hour}:00`;
}

function reminderTime(row, fallback = '00:00') {
  if (row && typeof row.time === 'string' && row.time.trim()) {
    return normalizeTimeString(row.time, fallback);
  }

  const hour = String(row?.hour ?? '0').padStart(2, '0');
  const minute = String(row?.minute ?? '0').padStart(2, '0');
  return normalizeTimeString(`${hour}:${minute}`, fallback);
}

function typeLabelFor(key) {
  return REMINDER_TYPES.find((item) => item.key === key)?.label || key;
}

function scopeLabelFor(key) {
  return TARGET_SCOPES.find((item) => item.key === key)?.label || key;
}

function dayLabelFor(code) {
  return DAY_OPTIONS.find((item) => item.key === code)?.label || code;
}

export default function ParentReminderSettings({ schoolId, onError, onSuccess }) {
  const [classes, setClasses] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [reminderType, setReminderType] = useState('reading_speed');
  const [targetScope, setTargetScope] = useState('classes');
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [timeValue, setTimeValue] = useState(DEFAULT_TIME);
  const [enabled, setEnabled] = useState(true);
  const [selectedDays, setSelectedDays] = useState(['mon', 'tue', 'wed', 'thu', 'fri']);

  const [editingId, setEditingId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const isWeeklyDayLocked = reminderType === 'weekly_engagement';
  const selectedTypeHelp = REMINDER_TYPE_HELP[reminderType] || '';

  const loadClasses = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}`));
      if (!res.ok) throw new Error('load-classes');
      const data = await res.json();
      setClasses(Array.isArray(data) ? data : []);
    } catch (error) {
      onError && onError('Şubeler yüklenemedi');
    }
  }, [schoolId, onError]);

  const loadReminders = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/parent-reminders/${encodeURIComponent(schoolId)}/items`));
      if (!res.ok) throw new Error('load-reminders');
      const data = await res.json();
      setReminders(data.reminders || []);
    } catch (error) {
      onError && onError('Hatırlatmalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [schoolId, onError]);

  useEffect(() => {
    loadClasses();
    loadReminders();
  }, [loadClasses, loadReminders]);

  const classOptions = useMemo(
    () =>
      (classes || []).map((item) => ({
        ...item,
        id: item.id,
        label: `${item.name || item.code || ''}`.trim() || String(item.id),
        grade_level: item.grade_level || '',
      })),
    [classes],
  );

  const gradeOptions = useMemo(() => {
    const values = new Set();
    (classes || []).forEach((item) => {
      const grade = String(item.grade_level || '').trim();
      if (grade) values.add(grade);
    });
    return Array.from(values)
      .sort()
      .map((grade) => ({ key: grade, label: grade }));
  }, [classes]);

  const resetForm = useCallback(() => {
    setReminderType('reading_speed');
    setTargetScope('classes');
    setSelectedClasses([]);
    setSelectedGrades([]);
    setTimeValue(DEFAULT_TIME);
    setEnabled(true);
    setSelectedDays(['mon', 'tue', 'wed', 'thu', 'fri']);
    setEditingId(null);
    setEditDialogOpen(false);
  }, []);

  const handleReminderTypeChange = useCallback((nextType) => {
    setReminderType(nextType);
    if (nextType === 'weekly_engagement') {
      setSelectedDays([...WEEKLY_LOCKED_DAYS]);
    }
  }, []);

  const buildBody = useCallback(() => {
    const effectiveTime = isWeeklyDayLocked
      ? normalizeHourlyTimeString(timeValue, DEFAULT_TIME)
      : normalizeTimeString(timeValue, DEFAULT_TIME);
    const effectiveDays = isWeeklyDayLocked ? [...WEEKLY_LOCKED_DAYS] : selectedDays;
    const base = {
      reminder_type: reminderType,
      target_scope: targetScope,
      time: effectiveTime,
      enabled,
      days: effectiveDays,
    };

    if (targetScope === 'classes') {
      return {
        ...base,
        class_ids: (selectedClasses || []).map((item) => item.id),
        grade_levels: [],
      };
    }

    return {
      ...base,
      class_ids: [],
      grade_levels: (selectedGrades || []).map((item) => item.key),
    };
  }, [enabled, isWeeklyDayLocked, reminderType, selectedClasses, selectedDays, selectedGrades, targetScope, timeValue]);

  const handleSave = useCallback(async () => {
    if (!schoolId || !timeValue) return;

    if (targetScope === 'classes' && (selectedClasses || []).length === 0) {
      onError && onError('En az bir şube seçin');
      return;
    }
    if (targetScope === 'grade_levels' && (selectedGrades || []).length === 0) {
      onError && onError('En az bir sınıf seviyesi seçin');
      return;
    }

    setSaving(true);
    try {
      const body = buildBody();
      let res;
      if (editingId) {
        res = await fetchJson(
          apiUrl(`/admin/parent-reminders/${encodeURIComponent(schoolId)}/items/${encodeURIComponent(editingId)}`),
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          },
        );
      } else {
        res = await fetchJson(apiUrl(`/admin/parent-reminders/${encodeURIComponent(schoolId)}/items`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }
      if (!res.ok) throw new Error('save');

      const data = await res.json();
      setReminders(data.reminders || []);
      onSuccess && onSuccess(editingId ? 'Hatırlatma güncellendi' : 'Hatırlatma eklendi');
      resetForm();
    } catch (error) {
      onError && onError('Hatırlatma kaydedilemedi');
    } finally {
      setSaving(false);
    }
  }, [
    buildBody,
    editingId,
    onError,
    onSuccess,
    resetForm,
    schoolId,
    selectedClasses,
    selectedGrades,
    targetScope,
    timeValue,
  ]);

  const startEdit = useCallback(
    (row) => {
      const nextType = row.reminder_type || 'reading_speed';
      const nextScope = row.target_scope || 'classes';
      const rowClassIds = (row.class_ids || []).map((value) => String(value));
      const rowGrades = (row.grade_levels || []).map((value) => String(value));

      setEditingId(row.id);
      setReminderType(nextType);
      setTargetScope(nextScope);
      setTimeValue(
        nextType === 'weekly_engagement'
          ? normalizeHourlyTimeString(reminderTime(row, DEFAULT_TIME), DEFAULT_TIME)
          : reminderTime(row, DEFAULT_TIME),
      );
      setEnabled(Boolean(row.enabled));
      setSelectedDays(nextType === 'weekly_engagement' ? [...WEEKLY_LOCKED_DAYS] : row.days || ['mon', 'tue', 'wed', 'thu', 'fri']);
      setSelectedClasses(classOptions.filter((item) => rowClassIds.includes(String(item.id))));
      setSelectedGrades(gradeOptions.filter((item) => rowGrades.includes(String(item.key))));
      setEditDialogOpen(true);
    },
    [classOptions, gradeOptions],
  );

  const handleDelete = useCallback(
    async (id) => {
      if (!schoolId || !id) return;
      setSaving(true);
      try {
        const res = await fetchJson(
          apiUrl(`/admin/parent-reminders/${encodeURIComponent(schoolId)}/items/${encodeURIComponent(id)}`),
          { method: 'DELETE' },
        );
        if (!res.ok) throw new Error('delete');

        const data = await res.json();
        setReminders(data.reminders || []);
        onSuccess && onSuccess('Hatırlatma silindi');
      } catch (error) {
        onError && onError('Hatırlatma silinemedi');
      } finally {
        setSaving(false);
        setDeleteDialogOpen(false);
        setDeleteId(null);
      }
    },
    [onError, onSuccess, schoolId],
  );

  const handleToggle = useCallback(
    async (id, value) => {
      const row = reminders.find((item) => String(item.id) === String(id));
      if (!row) return;

      setSaving(true);
      try {
        const body = {
          reminder_type: row.reminder_type,
          target_scope: row.target_scope,
          class_ids: row.class_ids || [],
          grade_levels: row.grade_levels || [],
          time: reminderTime(row),
          enabled: value,
          days: row.days || ['mon', 'tue', 'wed', 'thu', 'fri'],
        };

        const res = await fetchJson(
          apiUrl(`/admin/parent-reminders/${encodeURIComponent(schoolId)}/items/${encodeURIComponent(id)}`),
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          },
        );
        if (!res.ok) throw new Error('toggle');

        const data = await res.json();
        setReminders(data.reminders || []);
      } catch (error) {
        onError && onError('Durum güncellenemedi');
      } finally {
        setSaving(false);
      }
    },
    [onError, reminders, schoolId],
  );

  const renderTimeField = (keyPrefix) => (
    isWeeklyDayLocked ? (
      <TextField
        key={`${keyPrefix}-weekly-hourly`}
        select
        label="Saat"
        value={normalizeHourlyTimeString(timeValue, DEFAULT_TIME)}
        onChange={(e) => setTimeValue(normalizeHourlyTimeString(e.target.value, DEFAULT_TIME))}
        helperText="Haftalık özet için gün Pazartesi olarak sabittir, saat 24 saat formatında tam saat olarak seçilir."
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
      >
        {HOURLY_TIME_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </TextField>
    ) : (
      <TextField
        key={`${keyPrefix}-free`}
        type="time"
        label="Saat"
        value={normalizeTimeString(timeValue, DEFAULT_TIME)}
        onChange={(e) => setTimeValue(normalizeTimeString(e.target.value, DEFAULT_TIME))}
        disabled={false}
        helperText="Veritabanı değeri HH:MM olarak gösterilir ve kaydedilir."
        InputLabelProps={{ shrink: true }}
        inputProps={{ step: 60 }}
      />
    )
  );

  const renderTargetPicker = () =>
    targetScope === 'classes' ? (
      <Autocomplete
        multiple
        options={classOptions}
        value={selectedClasses}
        onChange={(_, value) => setSelectedClasses(value)}
        getOptionLabel={(option) => option?.label || ''}
        renderInput={(params) => <TextField {...params} label="Şube seçin" />}
      />
    ) : (
      <Autocomplete
        multiple
        options={gradeOptions}
        value={selectedGrades}
        onChange={(_, value) => setSelectedGrades(value)}
        getOptionLabel={(option) => option?.label || ''}
        renderInput={(params) => <TextField {...params} label="Sınıf seviyesi seçin" />}
      />
    );

  const renderDayChips = () => (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      {DAY_OPTIONS.map((day) => {
        const active = selectedDays.includes(day.key);
        return (
          <Chip
            key={day.key}
            label={day.label}
            color={active ? 'primary' : 'default'}
            variant={active ? 'filled' : 'outlined'}
            disabled={isWeeklyDayLocked}
            onClick={
              isWeeklyDayLocked
                ? undefined
                : () => {
                    setSelectedDays((prev) =>
                      prev.includes(day.key) ? prev.filter((item) => item !== day.key) : [...prev, day.key],
                    );
                  }
            }
          />
        );
      })}
    </Stack>
  );

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsActiveIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Veli Hatırlatmaları
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Seçtiğiniz saatte otomatik hatırlatma gönderilir.
          </Typography>
        </Box>

        <Alert severity="info">
          <div>
            <strong>24 saat içinde yazışma varsa:</strong> detaylı mesaj (gerektiğinde butonlu interactive mesaj).
          </div>
          <div>
            <strong>24 saat dışında:</strong> UTILITY şablon kullanılır (
            <strong>parent_reading_speed_reminder_v11</strong> / <strong>parent_study_log_reminder_v2</strong> /{' '}
            <strong>parent_finished_books_reminder_v2</strong> /{' '}
            <strong>parent_mini_task_reminder_v1</strong> /{' '}
            <strong>parent_weekly_engagement_report_v2</strong>).
          </div>
        </Alert>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr 1fr 1fr' }, gap: 2 }}>
          <TextField
            select
            label="Hatırlatma türü"
            value={reminderType}
            SelectProps={{ native: true }}
            onChange={(e) => handleReminderTypeChange(e.target.value)}
          >
            {REMINDER_TYPES.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </TextField>

          <TextField
            select
            label="Hedef"
            value={targetScope}
            SelectProps={{ native: true }}
            onChange={(e) => {
              setTargetScope(e.target.value);
              setSelectedClasses([]);
              setSelectedGrades([]);
            }}
          >
            {TARGET_SCOPES.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </TextField>

          {renderTimeField('create')}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <Typography>Etkin</Typography>
          </Box>
        </Box>

        {selectedTypeHelp ? <Alert severity="info">{selectedTypeHelp}</Alert> : null}

        {renderTargetPicker()}
        {renderDayChips()}

        <Stack direction="row" spacing={1}>
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleSave} disabled={saving}>
            {editingId ? 'Güncelle' : 'Ekle'}
          </Button>
          <Button variant="outlined" onClick={resetForm} disabled={saving}>
            Temizle
          </Button>
          <Box sx={{ flex: 1 }} />
          {saving && <CircularProgress size={20} />}
        </Stack>

        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Mevcut Hatırlatmalar
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : reminders.length === 0 ? (
            <Alert severity="warning">Henüz bir hatırlatma yok.</Alert>
          ) : (
            <Stack spacing={1}>
              {reminders.map((row) => (
                <Paper key={row.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700 }}>{typeLabelFor(row.reminder_type)}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {scopeLabelFor(row.target_scope)} • {reminderTime(row)} • {(row.days || []).map(dayLabelFor).join(', ') || 'Pzt-Cum'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {row.target_scope === 'classes'
                          ? `Şubeler: ${(row.class_ids || []).join(', ') || '-'}`
                          : `Sınıf seviyeleri: ${(row.grade_levels || []).join(', ') || '-'}`}
                      </Typography>
                    </Box>

                    <Switch checked={Boolean(row.enabled)} onChange={(e) => handleToggle(row.id, e.target.checked)} />
                    <IconButton onClick={() => startEdit(row)} size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        setDeleteId(row.id);
                        setDeleteDialogOpen(true);
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>

        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Hatırlatmayı Düzenle</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Düzenlemeler kaydedildiğinde hatırlatma hedefleri ve günleri güncellenir.
            </Alert>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
              <TextField
                select
                label="Hatırlatma türü"
                value={reminderType}
                SelectProps={{ native: true }}
                onChange={(e) => handleReminderTypeChange(e.target.value)}
              >
                {REMINDER_TYPES.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))}
              </TextField>

              <TextField
                select
                label="Hedef"
                value={targetScope}
                SelectProps={{ native: true }}
                onChange={(e) => {
                  setTargetScope(e.target.value);
                  setSelectedClasses([]);
                  setSelectedGrades([]);
                }}
              >
                {TARGET_SCOPES.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))}
              </TextField>

              {renderTimeField('edit')}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                <Typography>Etkin</Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>{renderTargetPicker()}</Box>
            <Box sx={{ mt: 2 }}>{renderDayChips()}</Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
              İptal
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              Kaydet
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Hatırlatmayı Sil</DialogTitle>
          <DialogContent>
            <Typography>Bu hatırlatma silinsin mi?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
              İptal
            </Button>
            <Button color="error" variant="contained" onClick={() => handleDelete(deleteId)} disabled={saving}>
              Sil
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Paper>
  );
}
